import { useState } from 'react';
import { D, fmt, fmtCompactUsd, fmtPct, fmtPrice, pairLabel } from '@platform/shared';
import { Link } from '../lib/router';
import { openAlertBuilder } from '../stores/alerts';
import { useCryptoMarket } from '../stores/cryptoMarket';
import { useInstrument, useQuote, useTicker } from '../stores/market';
import { toggleSearch, useSearchOpen } from '../stores/search';
import { EventChip } from './EventNotice';
import { InstrumentIcon } from './InstrumentIcon';
import { PairName } from './PairName';
import { usePriceMove } from './PriceMove';
import { Change, cx, Icon, LevBadge, showsVolume, unitLabel } from './ui';

function Stat({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={cx('flex flex-col gap-0.5', className)}>
      <span className="text-[11px] text-muted">{label}</span>
      <span className="num text-[12.5px]">{children}</span>
    </div>
  );
}

export function InstrumentHeader({ symbol }: { symbol: string }) {
  const inst = useInstrument(symbol);
  const ticker = useTicker(symbol);
  const quote = useQuote(symbol);
  const market = useCryptoMarket(symbol);
  const move = usePriceMove(symbol);
  const open = useSearchOpen();
  // Phones (< sm) show pair, price and bid/ask; the other stats sit behind "More".
  const [details, setDetails] = useState(false);
  const dp = inst?.priceDecimals ?? 2;
  const ccy = inst?.priceCcy ?? 'USD';
  const swapCls = (r: string | undefined) => (r && D.from(r).isNeg() ? 'text-down' : 'text-up');

  return (
    <div className="relative flex flex-wrap items-center gap-x-7 gap-y-1.5 border-b border-line px-4 py-2.5 sm:gap-y-2 sm:py-3 md:px-5">
      <div className="flex w-full flex-wrap items-center justify-between gap-x-3 sm:contents">
        <button
          type="button"
          onClick={(e) => toggleSearch(e.currentTarget)}
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-label={`${pairLabel(symbol)}, choose another market`}
          className={cx('-ml-1.5 flex items-center gap-1.5 rounded-[10px] py-1.5 pr-2.5 pl-1.5 text-fg hover:bg-raised sm:gap-2', open ? 'bg-raised' : 'bg-transparent')}
        >
          <InstrumentIcon symbol={symbol} size={28} />
          <PairName symbol={symbol} assetClass={inst?.assetClass} className="text-lg font-semibold tracking-tight sm:text-xl" />
          {inst && <LevBadge lev={inst.maxLeverage} />}
          <Icon name="chevron" size={14} className={cx('transition-transform', open && 'rotate-180')} />
        </button>
        <div className="flex items-baseline gap-2">
          <span className="num text-lg font-medium sm:text-xl">{quote ? fmtPrice(quote.mark, dp, ccy) : '—'}</span>
          {move && (
            <span title={move.title} className="flex items-baseline gap-1">
              <Change pct={move.pct}>{fmtPct(move.pct.toFixed(2))}</Change>
              <span className="text-[11px] text-muted">{move.label}</span>
            </span>
          )}
          <Link
            to={`/markets/${symbol}`}
            aria-label={`About ${pairLabel(symbol)}: stats, market data and FAQ`}
            title="About this market"
            className="ml-1 grid size-7 place-items-center self-center rounded-lg border border-line-strong text-muted no-underline hover:border-accent hover:text-fg"
          >
            <Icon name="info" size={13} />
          </Link>
          <button
            type="button"
            onClick={() => openAlertBuilder({ symbol })}
            aria-label={`Set an alert on ${pairLabel(symbol)}`}
            title="Set an alert"
            className="grid size-7 place-items-center self-center rounded-lg border border-line-strong text-muted hover:border-accent hover:text-fg"
          >
            <Icon name="alert" size={13} />
          </button>
        </div>
      </div>
      <div className="num flex w-full items-center justify-between text-[12.5px] sm:hidden">
        <span>
          <span className="font-sans text-muted">Bid / Ask </span>
          <span className="text-up">{quote ? fmt(quote.bid, dp) : '—'}</span> / <span className="text-down">{quote ? fmt(quote.ask, dp) : '—'}</span>
        </span>
        <button type="button" onClick={() => setDetails((d) => !d)} aria-expanded={details} className="flex items-center gap-1 bg-transparent font-sans text-muted">
          {details ? 'Less' : 'More'}
          <Icon name="chevron" size={12} className={cx('transition-transform', details && 'rotate-180')} />
        </button>
      </div>
      <div className={cx(details ? 'grid w-full grid-cols-2 gap-x-4 gap-y-2 pb-1' : 'hidden', 'sm:contents')}>
        <Stat label="Mark Price">{quote ? fmt(quote.mark, dp) : '—'}</Stat>
        <Stat label="Bid / Ask" className="max-sm:hidden">
          <span className="text-up">{quote ? fmt(quote.bid, dp) : '—'}</span> / <span className="text-down">{quote ? fmt(quote.ask, dp) : '—'}</span>
        </Stat>
        <Stat label="1 lot">{inst ? `${fmt(inst.lotSize, 0)} ${unitLabel(inst.unit, inst.lotSize)}` : '—'}</Stat>
        {showsVolume(inst?.assetClass) && <Stat label="24h Volume">{ticker ? fmtCompactUsd(ticker.vol24) : '—'}</Stat>}
        <Stat label="Open Interest">{ticker ? fmtCompactUsd(ticker.oi) : '—'}</Stat>
        {market?.volume24h != null && <Stat label="Market Vol 24h">{fmtCompactUsd(String(Math.round(market.volume24h)))}</Stat>}
        {market?.marketCap != null && <Stat label={market.rank ? `Market Cap #${market.rank}` : 'Market Cap'}>{fmtCompactUsd(String(Math.round(market.marketCap)))}</Stat>}
        <Stat label="Swap Buy / Sell" className="max-sm:col-span-2">
          <span className={swapCls(inst?.swapLongRate)}>{inst ? D.from(inst.swapLongRate).toFixed(4) : '—'}%</span>
          {' / '}
          <span className={swapCls(inst?.swapShortRate)}>{inst ? D.from(inst.swapShortRate).toFixed(4) : '—'}%</span>
        </Stat>
      </div>
      {ticker && !ticker.open && <span className="rounded-md bg-raised px-2 py-1 text-xs font-semibold text-warn">Market closed</span>}
      <EventChip symbol={symbol} />
      {ticker?.paused && (
        <span className="rounded-md bg-warn-bg px-2 py-1 text-xs font-semibold text-warn" title="No live price feed for this market right now">
          Indicative price
        </span>
      )}
    </div>
  );
}
